import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import { faWarning } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import ColoredTagChip from '../../../components/muiLegacy/Tables/KeywordTable/ColoredTagChip';
import { KeywordTableColumnTypes } from '../../../components/muiLegacy/Tables/KeywordTable/KeywordTableColumnPicker/models';
import { KeywordTableState } from '../../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import { VoxelPopover } from '../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../components/voxel/Popover/VoxelPopoverTrigger';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { TagGq } from '../../../graphql/projects/models/TagGq';
import { TagGroupGq } from '../../../graphql/projects/models/TagGroupGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { TagGroupStatsGq } from '../../../graphql/projects/queries/useGetTagGroupStatsGq';
import { formatNumber } from '../../../logic/otherUtilities/formatting/formatNumber';
import EditTagButton from './EditTagButton';

export interface TagProps {
  tag: TagGq;
  tagGroup: TagGroupGq;
  reload: () => Promise<unknown>;
  keywordTableState: KeywordTableState;
  tagGroupStats: TagGroupStatsGq;
}

export const Tag: React.FC<TagProps> = React.memo(
  ({ tag, tagGroup, reload, keywordTableState, tagGroupStats }) => {
    const [showWarning, setShowWarning] = useState(false);

    const tagMutation = projectsApiService.mutations.tags.useBulkTagKeywords();
    const handleTag = () => {
      // Show warning if relevant
      if (
        !keywordTableState.backendSelectionState.keywordIds.length &&
        !keywordTableState.backendSelectionState.invertSelection
      ) {
        setShowWarning(true);
        return;
      }

      // Show column if needed
      const tagGroupColumn = keywordTableState.columnPickerBag.columnState.find(
        (c) => c.id === tagGroup.id && c.type === KeywordTableColumnTypes.TagGroup
      );
      const tagGroupColumnShown = tagGroupColumn?.order === 0 || !!tagGroupColumn.order;

      if (!tagGroupColumnShown) {
        keywordTableState.columnPickerBag.showTagGroup(tagGroup.id);
      }

      // Apply tag
      setTagLoading(true);
      keywordTableState.handleResetSelection();
      tagGroupColumn && keywordTableState.scrollToColumn(tagGroupColumn);
      tagMutation({
        keywordQuery: {
          selecting: keywordTableState.backendSelectionState,
          filtering: keywordTableState.filterState,
        },
        tagGroupId: tagGroup.id,
        tagId: tag.id,
      })
        .then(reload)
        .finally(() => setTagLoading(false));
    };

    const [showEditMenu, setShowEditMenu] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [tagLoading, setTagLoading] = useState(false);

    // Looks weird if warning is  still open after editing
    useEffect(() => {
      if (showWarning && showEditMenu) {
        setShowWarning(false);
      }
    }, [showWarning]);

    // Stats
    const tagStats = tagGroupStats.tagStats.find((t) => t.tagId === tag.id);
    const hasTagFilter = useMemo(
      () => keywordTableState.filterState.tagFilters?.some((tf) => tf.tagId === tag.id),
      [keywordTableState.filterState, tag.id]
    );

    if (deleted) {
      return null;
    }

    return (
      <VoxelPopover placement="right" open={showWarning} onOpenChange={setShowWarning} disableClick>
        <VoxelPopoverTrigger>
          <button
            key={tag.id}
            onClick={(e) => handleTag()}
            className={`
              py-1 rounded cursor-pointer
              group
              flex flex-row items-center gap-2
              pl-1 pr-3 w-full
              hover:bg-voxel-gray-50
              ${showEditMenu && 'bg-gray-50'}
              hover:ring-2 ring-primary-300 hover:ring-gray-300 rounded
              active:ring-primary-400
              active:bg-gray-50
            `}
          >
            <ColoredTagChip
              loading={tagLoading}
              stopPropagation={false}
              key={tag.id}
              tag={tag}
              tagGroup={tagGroup}
              showClose={false}
              ellipsis
            />
            <div
              className={`${!showEditMenu && 'opacity-0 group-hover:opacity-100'}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <EditTagButton
                setDeleted={setDeleted}
                deleted={deleted}
                setShowEditMenu={setShowEditMenu}
                showEditMenu={showEditMenu}
                tag={tag}
                tagGroup={tagGroup}
                reload={reload}
              />
            </div>
            <div className="flex-grow" />
            <div
              className="flex flex-row gap-1 items-center"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div
                className={`${
                  !showEditMenu && !hasTagFilter && 'opacity-0 group-hover:opacity-100'
                }`}
              >
                <VoxelIconButton
                  size="xs"
                  icon={<FontAwesomeIcon icon={faFilter} size="sm" />}
                  color={hasTagFilter ? 'primary' : 'white'}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    keywordTableState.setFilterState((prev) => {
                      const next = { ...prev };
                      if (hasTagFilter) {
                        next.tagFilters = next.tagFilters.filter((tf) => tf.tagId !== tag.id);
                      } else {
                        next.tagFilters = [
                          ...next.tagFilters,
                          { tagGroupId: tag.tagGroupId, tagId: tag.id },
                        ];
                      }
                      return next;
                    });
                  }}
                />
              </div>
            </div>
            <div>
              {tagLoading && <CircularProgress size={10} />}
              {!tagLoading && (
                <VoxelText variant="table">
                  {formatNumber(tagStats?.keywordCount ?? 0, 0)}
                </VoxelText>
              )}
            </div>
          </button>
        </VoxelPopoverTrigger>
        <VoxelPopoverContent>
          <div className="px-4 py-3">
            <FontAwesomeIcon icon={faWarning} className="pr-2 text-warning-700" /> Select keywords
            before tagging
          </div>
        </VoxelPopoverContent>
      </VoxelPopover>
    );
  }
);

export default Tag;
